import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { GridPaginationModel } from '@mui/x-data-grid-premium'
import type { Invoice } from '@mynt/backoffice-api-spec/invoice'
import { useQueryClient } from '@tanstack/react-query'
import { useNotesContext } from 'contexts/Notes'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useCreateExternalPayment, useCreateInvoice, useCustomer, useCustomerCardAccounts, usePatchInvoice } from '../../api/react-query'
import { TooltipCustomerInvoices, TooltipCustomerSubscriptionFee } from '../../components/Tooltips'
import ButtonIconRegisterExternalPayment from '../../figma/images/buttonIconRegisterExternalPayment'
import ButtonIconRegisterInvoicePayment from '../../figma/images/buttonIconRegisterInvoicePayment'
import Spacings from '../../figma/tokens/Spacings'
import type { Currencies } from '../../flamingo/config/ConfigCurrencies'
import useConfigByCountry from '../../flamingo/hooks/useConfigByCountry'
import { prettifyEnumLabel } from '../../helpers/CreditOnCardHelpers'
import useMaterialNotification from '../../hooks/useMaterialNotification'
import FigmaBox from '../../mynt-components/components/FigmaBox'
import { UnstyledButton } from '../../mynt-components/components/StyledComponents'
import { When } from '../../mynt-components/components/When'
import { YYYY_MM_DD } from '../../tiger/Constants'
import {
  AuditLogAction,
  BookAction,
  CancelAction,
  ChangePaymentMethod,
  CreditAction,
  DebtCollectionAction,
  ReminderAction,
} from './actions'
import type { InvoiceFilters } from './api'
import { INVOICES_V3_KEY, useInvoicesPaginated } from './api'
import { InvoiceAuditLogDialog } from './InvoiceAuditLogDialog'
import { type AllOnSubmits, InvoiceModals, type TogglableModals } from './InvoiceModals'
import { InvoiceTable } from './InvoiceTable'
import { DEFAULT_FILTERS, type InvoiceTableFilters } from './types'

const formatAccountOCR = (accounts: { name: string; ocr: string }[]) => {
  if (accounts.length === 0) return 'No accounts found'
  if (accounts.length === 1) return accounts[0].ocr

  return accounts.map((account) => `${account.name} (${account.ocr})`).join(', ')
}

type InvoicesProps = {
  customerId?: string
  page: 'ACCOUNTING' | 'CUSTOMER'
  defaultFilters?: Partial<InvoiceTableFilters>
}

export const Invoices = ({ customerId, page, defaultFilters }: InvoicesProps) => {
  const [filters, setFilters] = useState<InvoiceTableFilters>({
    ...DEFAULT_FILTERS,
    ...defaultFilters,
  })
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 })

  const apiFilters: InvoiceFilters = {
    customerId,
    type: filters.type.length > 0 ? filters.type : undefined,
    state: filters.state.length > 0 ? filters.state : undefined,
    invoiceDateFrom: filters.invoiceDate.from?.format(YYYY_MM_DD),
    invoiceDateTo: filters.invoiceDate.to?.format(YYYY_MM_DD),
    settlementDateFrom: filters.settleDate.from?.format(YYYY_MM_DD),
    settlementDateTo: filters.settleDate.to?.format(YYYY_MM_DD),
    receiverName: filters.receiverName || undefined,
    invoiceNo: filters.invoiceNo || undefined,
    currency: filters.currency.length > 0 ? filters.currency : undefined,
    dueDateFrom: filters.dueDate.from?.format(YYYY_MM_DD),
    dueDateTo: filters.dueDate.to?.format(YYYY_MM_DD),
    grossTotalMin: filters.grossTotalMin,
    grossTotalMax: filters.grossTotalMax,
  }

  const { data, isFetching } = useInvoicesPaginated(apiFilters, {
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
  })

  const invoices = data?.data ?? []
  const rowCount = data?.pagination?.totalItems ?? 0

  const { data: customer } = useCustomer(customerId as string, { enabled: Boolean(customerId) })
  const { data: accounts = [] } = useCustomerCardAccounts(customerId as string, { enabled: Boolean(customerId) })

  const patchInvoice = usePatchInvoice()
  const createInvoice = useCreateInvoice()
  const externalPayment = useCreateExternalPayment(customerId ?? '')

  const notify = useMaterialNotification()
  const queryClient = useQueryClient()
  const { formatAmount } = useConfigByCountry()
  const [notesContext] = useNotesContext()

  const [modals, setModals] = useState<TogglableModals>({})
  const [auditInvoice, setAuditInvoice] = useState<{ invoiceId: string; customerId: string } | null>(null)

  const handleFilterChange = (newFilters: InvoiceTableFilters) => {
    setFilters(newFilters)
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
  }

  const invalidateInvoices = () => {
    setTimeout(() => queryClient.invalidateQueries({ queryKey: [INVOICES_V3_KEY] }), 1000)
  }

  const handleSubmit = async ([modal, data]: AllOnSubmits) => {
    switch (modal) {
      case 'invoicePayment': {
        return onSubmitInvoice(data)
      }

      case 'externalPayment': {
        return onSubmitExternalInvoice(data)
      }

      case 'invoicePenalty': {
        return onSubmitReminderFee(data)
      }

      default: {
        throw new Error(`missing submit handler for modal '${modal}'`)
      }
    }
  }

  const handleClose = () => setModals({})

  const onSubmitReminderFee = async (invoice: Parameters<typeof patchInvoice.mutateAsync>[0]) =>
    patchInvoice.mutateAsync(invoice).then(() => {
      notify(`Sent reminder`)
      setModals({})
      invalidateInvoices()
    })

  const onSubmitInvoice = async (invoice: Parameters<typeof createInvoice.mutateAsync>[0]) =>
    createInvoice.mutateAsync(invoice).then(() => {
      notify(`Created ${prettifyEnumLabel(invoice.type).toLowerCase()} invoice`)
      invalidateInvoices()
      setModals({})
    })

  const onSubmitExternalInvoice = async (values: {
    customerId: string
    ocr: string | undefined
    amount: number
    paymentDate: string
    description: string
    currency: Currencies
  }) =>
    externalPayment
      .mutateAsync({
        info: values.description,
        paymentDate: dayjs(values.paymentDate).format(YYYY_MM_DD),
        amount: { value: values.amount, currency: values.currency },
        ocr: values.ocr || values.customerId,
      })
      .then(() => {
        notify(`Created external payment`)
      })

  const handleActionClick = (invoice: Invoice) => async (updatedInvoice: Partial<Invoice>) =>
    patchInvoice.mutateAsync({ id: invoice.id, ...updatedInvoice })

  const tableActions = (invoice: Invoice, onClose) => [
    <BookAction key={1} invoice={invoice} onClick={handleActionClick(invoice)} />,
    <CancelAction key={2} invoice={invoice} onClick={handleActionClick(invoice)} />,
    <CreditAction key={3} invoice={invoice} onClick={handleActionClick(invoice)} />,
    <ReminderAction
      key={4}
      invoice={invoice}
      onClick={async () => {
        onClose()
        return Promise.resolve(setModals({ invoicePenalty: invoice }))
      }}
    />,
    <DebtCollectionAction key={5} invoice={invoice} onClick={handleActionClick(invoice)} />,
    <ChangePaymentMethod key={6} invoice={invoice} onClick={handleActionClick(invoice)} />,
    <AuditLogAction
      key={7}
      onClick={() => {
        onClose()
        setAuditInvoice({ invoiceId: invoice.id, customerId: invoice.customerId })
      }}
    />,
  ]

  return (
    <>
      {customer && <InvoiceModals customer={customer} modals={modals} onClose={handleClose} onSubmit={handleSubmit} />}
      {auditInvoice && (
        <InvoiceAuditLogDialog
          open={Boolean(auditInvoice)}
          onClose={() => setAuditInvoice(null)}
          customerId={auditInvoice.customerId}
          invoiceId={auditInvoice.invoiceId}
        />
      )}
      <FigmaBox align="flex-end" spacing={Spacings.tiny} bottom top fullWidth direction="row" justify="space-between">
        <FigmaBox>
          <h2>Customer invoices</h2>
          <Typography variant="subtitle1">
            <Typography style={{ display: 'inline' }} variant="subtitle1" fontWeight="bold">
              {'OCR: '}
            </Typography>
            {formatAccountOCR(accounts.map(({ name, ocr }) => ({ name, ocr })))}
          </Typography>
        </FigmaBox>
        <FigmaBox direction="row" gap={Spacings.min}>
          <TooltipCustomerInvoices
            content={
              <UnstyledButton onClick={() => setModals({ externalPayment: true })}>
                <ButtonIconRegisterExternalPayment />
              </UnstyledButton>
            }
          />
          <When is={Boolean(customerId)}>
            <TooltipCustomerSubscriptionFee
              content={
                <UnstyledButton onClick={() => setModals({ invoicePayment: true })}>
                  <ButtonIconRegisterInvoicePayment />
                </UnstyledButton>
              }
            />
          </When>
        </FigmaBox>
      </FigmaBox>
      <Box sx={{ maxWidth: notesContext.open ? '75vw' : '93vw' }}>
        <InvoiceTable
          page={page}
          invoices={invoices}
          filters={filters}
          onFilterChange={handleFilterChange}
          isLoading={isFetching || patchInvoice.isLoading}
          actions={tableActions}
          formatAmount={formatAmount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
        />
      </Box>
    </>
  )
}
